/**
 * Numeric dot-version compare — "2.0.9" < "2.0.10", unlike a plain string
 * compare. Used by the iOS marketing-version force-update fallback in
 * hooks/use-app-update.ts (when the server has no build number for iOS —
 * see apps/api's app-release-sync.ts) to decide whether the installed
 * version is below the server's `ios.minVersion`.
 *
 * A near-identical copy lives server-side in
 * apps/api/src/lib/version-compare.ts — kept as two small files rather
 * than a shared package because the two runtimes don't otherwise share
 * code, and this is ~15 lines that must never disagree in behaviour
 * (tested with the same cases on both sides).
 */

/** Parses "2.0.9" → [2,0,9]. Anything with a non-digit segment (a `-beta`
 *  suffix, empty string, etc.) is treated as unparsable → null, so callers
 *  fail open rather than guess. */
export function parseVersion(v: string): number[] | null {
  const trimmed = v.trim();
  if (!/^\d+(\.\d+)*$/.test(trimmed)) return null;
  return trimmed.split(".").map((n) => parseInt(n, 10));
}

/** -1 if a<b, 0 if equal, 1 if a>b, comparing segment by segment and
 *  padding the shorter with zeros ("2.0" == "2.0.0"). */
export function compareVersions(a: number[], b: number[]): -1 | 0 | 1 {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

/** True when `installed` is strictly below `min`. Unparsable input on
 *  either side → false (never force on data we can't read). */
export function isVersionBelow(installed: string, min: string): boolean {
  const a = parseVersion(installed);
  const b = parseVersion(min);
  if (!a || !b) return false;
  return compareVersions(a, b) < 0;
}
